import { FlightFromAiSidePanel } from "components/SidePanel/Flight/FlightFromAiSidePanel";
import { FormatArrivalTime } from "flights/lookup/FlightSearchResult";
import moment from "moment";
import React, { useState } from "react";
import { Message } from "semantic-ui-react";
import { getAirportCode } from "shared/utils/flightUtils";
import { formatTime, formatWrittenDayMonthYearShort } from "utilities";
import { ContactForExtractorSession } from "./ContactForExtractorSession";
import { FlightAiRandomLoadingMessages } from "components/RandomLoadingMessages/RandomLoadingMessage";
import { useElementHeight } from "utilities/hookutils";
import { ButtonSecondary, ButtonTertiary } from "components/Atoms/Button";
import { useMutation } from "@tanstack/react-query";
import { FlightSidePanel } from "components/SidePanel/Flight/FlightSidePanel";
import { PhosphorCheckCircle, PhosphorWarningInline } from "components/Atoms/PhosphorIcon";
import { Badge } from "components/Atoms/Badge";

const FlightFromTo = ({ flight }: { flight: server.dto.ExtractedFlight }) => {
  const isFlightCreated = flight.lifeCycle === "flightCreated";

  return <div className="flight-parser-session-result-grid">
    <div className="lifecycle-indicator" style={{ visibility: isFlightCreated ? "visible" : "hidden" }}>
      <PhosphorCheckCircle iconColor="green" iconSize="regular" iconWeight="fill" />
    </div>
    <div className="flight-identity">
      <div className="flight-code">
        {isFlightCreated
          ? <FlightSidePanel
            flightId={flight.flightId}
            trigger={<a>{flight.flightDesignator}</a>}
          />
          : <FlightFromAiSidePanel
            extractedFlightId={flight.extractedFlightId}
            trigger={<a>{flight.flightDesignator}</a>}
          />}
      </div>
      <div className="artist-name">
        {flight.artist.name}
      </div>
    </div>
    <div className="departure-summary">
      <div className="flex gap">
        <div className="departure-date info-text-regular grow1">
          {formatWrittenDayMonthYearShort(flight.dateTime.startDisplay)}
        </div>
        <div className="departure-time nowrap info-text-regular">
          {formatTime(flight.dateTime.startDisplay)}
        </div>
      </div>
      {flight.departureAirport && <div className="departure-airport">
        <div className="subtitle-text-regular margin-bottom-small">
          {flight.departureAirport.city} [{getAirportCode(flight.departureAirport)}]
        </div>
      </div>}
    </div>
    {!isFlightCreated && <>
      <div className="arrow">
        <i className="icon-arrow-right small" />
      </div>
      <div className="arrives-at">
        <div className="info-text-regular">
          <FormatArrivalTime
            departure={moment.utc(flight.dateTime.startDisplay)}
            arrival={moment.utc(flight.dateTime.endDisplay)}
          />
        </div>
        {flight.arrivalAirport && <div className="subtitle-text-regular margin-bottom-small">
          {flight.arrivalAirport.city} [{getAirportCode(flight.arrivalAirport)}]
        </div>}
      </div>
      <div className="passenger padding-bottom">
        <div className="flex-column gap">
          {flight.passengers.map((p, i) => <ContactForExtractorSession
            key={`${flight.extractedFlightId}-${i}`}
            contactSmall={p.passengerContact}
            extractedFlightPassengerId={p.extractedFlightPassengerId}
            contactName={p.passengerName}
          />)}
        </div>
      </div>
      <div className="review-button">
        {flight.lifeCycle === "hasUnmappedPassengers" && <Badge
          iconComp={PhosphorWarningInline}
          label={"Passenger"}
          sublabel={"Match contact"}
          variant="yellow"
        />}
        {flight.lifeCycle === "readyForApproval" && <FlightFromAiSidePanel
          extractedFlightId={flight.extractedFlightId}
          trigger={<ButtonTertiary
            label={RESX.DocumentLifeCycle.Review}
          />}
        />}
      </div>
    </>}
  </div>;
};

interface Props {
  item: server.dto.ExtractorSession
}

export const ExtractorSessionFlightsTableRow = ({ item }: Props) => {
  const heightHelper = useElementHeight();
  const [loadingForItem, setLoadingForItem] = useState<server.dto.ExtractorSession>();

  const height = (item.lifeCycle === "waitingForProcessing" || item.lifeCycle === "started") ? heightHelper.height : undefined;

  const tryAgainMutation = useMutation({
    mutationFn: window.CoreApi.Extractor.ResendSession,
    onError: () => setLoadingForItem(undefined),
  });

  const isRetrying = loadingForItem === item;

  return <div style={{ height }} ref={heightHelper.ref}>
    {(item.lifeCycle === "waitingForProcessing" || item.lifeCycle === "started") && <FlightAiRandomLoadingMessages />}
    {item.lifeCycle === "errorFatal" && <div className="flex-column">
      <div className="message ui red flex-column gap s1_small" title={item.debugInfo}>
        <div>Something went wrong, maybe because the data could not be processed.<br />
          <br />When you believe this to be incorrect, please email support@systemonesoftware.com with the data and a short explanation.</div>
      </div>
    </div>}
    {item.lifeCycle === "errorTransient" && <div className="flex-column">
      <div className="message ui red flex-column gap">
        <div>There was an error with processing. Perhaps it is a temporary issue.</div>
        <div>
          <ButtonSecondary
            onClick={() => {
              setLoadingForItem(item);
              tryAgainMutation.mutate({ extractorSessionId: item.extractorSessionId });
            }}
            loading={isRetrying}
            disabled={isRetrying}
            label="Try again"
          />
        </div>
      </div>
    </div>}
    {item.lifeCycle === "waitingForUserAction" && <div className="flight-parser-session-result-list">
      {item.flights.map(flight => <FlightFromTo key={flight.extractedFlightId} flight={flight} />)}
    </div>}
    {item.lifeCycle === "completed" && <div className="flex-column"><Message color="green">
      This flight plan has been processed and no further actions are needed. You can find the flights in the main flight list.
    </Message></div>}
  </div>;
}
